import Switch from '@mui/material/Switch';
import React from 'react';

import { noop } from '../../../util/appUtil';
import styles from './emailNotifications.module.css';

/**
 * A switch and its label, for the Status cell of one subscription.
 *
 * Status lives on the individual subscription, so the control does too -- on the rows
 * inside an expanded address rather than on the address itself. An address-level
 * switch would have to write every row behind it, which is a different action than
 * the one the cell appears to offer once the rows are visible and individually
 * settable.
 *
 * Purely presentational: it takes a flag and reports the flag it is being moved to.
 *
 * Two details this shares with the toggle it replaced. The row it sits in opens the
 * editor on click, so the switch stops propagation -- without that, toggling would
 * also open the modal over the top of it. And the label is rendered beside the switch
 * rather than relying on colour alone, matching how Feature Flags reads ("On") and
 * keeping the column legible when scanned.
 */
export default function StatusToggle(props: StatusToggleProps) {
  const { active, ariaLabel, canEdit = false, onToggle = noop, dataTest, statusLabel } = props;

  const stopRowClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <span
      className={styles.statusCell}
      onClick={stopRowClick}
      onKeyDown={stopRowClick}
      role="presentation"
    >
      <Switch
        checked={active}
        disabled={!canEdit}
        onChange={() => onToggle(!active)}
        inputProps={
          {
            'aria-label': ariaLabel,
            'data-test': dataTest ? `${dataTest}-switch` : undefined,
          } as any
        }
      />
      <span
        className={active ? undefined : styles.statusInactive}
        data-test={dataTest ? `${dataTest}-label` : undefined}
      >
        {statusLabel ?? (active ? 'Active' : 'Inactive')}
      </span>
    </span>
  );
}

export interface StatusToggleProps {
  active: boolean;
  /** Names what is being switched, since the switch itself carries no visible text. */
  ariaLabel: string;
  canEdit?: boolean;
  /** Receives the state the switch is being moved to. */
  onToggle?: (active: boolean) => void;
  /**
   * Overrides the Active/Inactive wording. A switch has two positions but a region
   * rollup has three states, so a region that is partly on reads "Partial" beside a
   * switch that sits off -- which is what clicking it to turn the region fully on
   * invites.
   */
  statusLabel?: string;
  dataTest?: string;
}
